/**
 * Validation Hooks
 *
 * React hooks for form validation with real-time feedback.
 * These hooks wrap the pure validation functions from lib/validation.ts
 * to provide stateful validation for React components.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  validateIdeologyVector,
  validateIdeologyAxisValue,
  validatePartyForm,
  validateCandidateForm,
  type ValidationResult,
  type FormValidationResult,
} from '@/lib/validation';

import type { IdeologyVector } from '@/types';
import { IDEOLOGY_AXIS_COUNT } from '@/constants/ideology';

// ============================================================================
// Ideology Validation Hook
// ============================================================================

export interface UseIdeologyValidationResult {
  /** Validate the current vector and get errors */
  validate: (vector: IdeologyVector) => ValidationResult;
  /** Validate a single axis value for real-time feedback */
  validateAxis: (value: number, axisIndex: number) => string | null;
  /** Get error messages per axis (indexed 0-6) */
  axisErrors: (string | null)[];
  /** Whether the current vector is fully valid */
  isValid: boolean;
  /** Set axis errors for display */
  setAxisErrors: (errors: (string | null)[]) => void;
  /** Clear all axis errors */
  clearErrors: () => void;
}

/**
 * Hook for validating ideology vectors with per-axis error tracking.
 * Useful for IdeologyEditor component.
 */
export function useIdeologyValidation(): UseIdeologyValidationResult {
  const [axisErrors, setAxisErrors] = useState<(string | null)[]>(
    Array(IDEOLOGY_AXIS_COUNT).fill(null)
  );

  const validate = useCallback((vector: IdeologyVector): ValidationResult => {
    const result = validateIdeologyVector(vector);

    // Update axis errors based on validation result
    const newErrors: (string | null)[] = Array(IDEOLOGY_AXIS_COUNT).fill(null);

    if (!result.valid) {
      // Parse errors to find which axes are invalid
      for (const error of result.errors) {
        // Match patterns like "Ideology axis 0 must be..."
        const match = error.match(/axis (\d+)/);
        if (match) {
          const index = parseInt(match[1], 10);
          if (index >= 0 && index < IDEOLOGY_AXIS_COUNT) {
            newErrors[index] = error;
          }
        }
      }
    }

    setAxisErrors(newErrors);
    return result;
  }, []);

  const validateAxis = useCallback((value: number, axisIndex: number): string | null => {
    const error = validateIdeologyAxisValue(value, axisIndex);

    // Update just this axis error
    setAxisErrors((prev) => {
      const next = [...prev];
      next[axisIndex] = error;
      return next;
    });

    return error;
  }, []);

  const clearErrors = useCallback(() => {
    setAxisErrors(Array(IDEOLOGY_AXIS_COUNT).fill(null));
  }, []);

  const isValid = useMemo(() => axisErrors.every((e) => e === null), [axisErrors]);

  return {
    validate,
    validateAxis,
    axisErrors,
    isValid,
    setAxisErrors,
    clearErrors,
  };
}

// ============================================================================
// Party Form Validation Hook
// ============================================================================

export interface UsePartyFormValidationResult {
  /** Validate the entire form */
  validate: (data: {
    name: string;
    abbreviation: string;
    color: string;
    slogan?: string;
    ideology_vector: unknown;
  }) => FormValidationResult;
  /** Field-level error messages */
  fieldErrors: Record<string, string>;
  /** Whether the form is currently valid */
  isValid: boolean;
  /** Set errors manually */
  setFieldErrors: (errors: Record<string, string>) => void;
  /** Clear all errors */
  clearErrors: () => void;
  /** Clear error for a specific field */
  clearFieldError: (field: string) => void;
  /** Get error for a specific field */
  getFieldError: (field: string) => string | undefined;
}

/**
 * Hook for validating party creation/edit forms.
 */
export function usePartyFormValidation(): UsePartyFormValidationResult {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const validate = useCallback(
    (data: {
      name: string;
      abbreviation: string;
      color: string;
      slogan?: string;
      ideology_vector: unknown;
    }): FormValidationResult => {
      const result = validatePartyForm(data);
      setFieldErrors(result.fieldErrors);
      return result;
    },
    []
  );

  const clearErrors = useCallback(() => {
    setFieldErrors({});
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const getFieldError = useCallback(
    (field: string): string | undefined => {
      return fieldErrors[field];
    },
    [fieldErrors]
  );

  const isValid = useMemo(() => Object.keys(fieldErrors).length === 0, [fieldErrors]);

  return {
    validate,
    fieldErrors,
    isValid,
    setFieldErrors,
    clearErrors,
    clearFieldError,
    getFieldError,
  };
}

// ============================================================================
// Candidate Form Validation Hook
// ============================================================================

export interface UseCandidateFormValidationResult {
  /** Validate the entire form */
  validate: (data: {
    name: string;
    age: number;
    ideology_vector: unknown;
    loyalty?: number;
    recognition?: number;
    charisma?: number;
    admin_skill?: number;
    debate_skill?: number;
    scandal_prone?: number;
    ambition?: number;
  }) => FormValidationResult;
  /** Field-level error messages */
  fieldErrors: Record<string, string>;
  /** Whether the form is currently valid */
  isValid: boolean;
  /** Set errors manually */
  setFieldErrors: (errors: Record<string, string>) => void;
  /** Clear all errors */
  clearErrors: () => void;
  /** Clear error for a specific field */
  clearFieldError: (field: string) => void;
  /** Get error for a specific field */
  getFieldError: (field: string) => string | undefined;
}

/**
 * Hook for validating candidate creation/edit forms.
 */
export function useCandidateFormValidation(): UseCandidateFormValidationResult {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const validate = useCallback(
    (data: {
      name: string;
      age: number;
      ideology_vector: unknown;
      loyalty?: number;
      recognition?: number;
      charisma?: number;
      admin_skill?: number;
      debate_skill?: number;
      scandal_prone?: number;
      ambition?: number;
    }): FormValidationResult => {
      const result = validateCandidateForm(data);
      setFieldErrors(result.fieldErrors);
      return result;
    },
    []
  );

  const clearErrors = useCallback(() => {
    setFieldErrors({});
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const getFieldError = useCallback(
    (field: string): string | undefined => {
      return fieldErrors[field];
    },
    [fieldErrors]
  );

  const isValid = useMemo(() => Object.keys(fieldErrors).length === 0, [fieldErrors]);

  return {
    validate,
    fieldErrors,
    isValid,
    setFieldErrors,
    clearErrors,
    clearFieldError,
    getFieldError,
  };
}

// ============================================================================
// Generic Field Validation Hook
// ============================================================================

export interface UseFieldValidationResult<T> {
  /** Current value */
  value: T;
  /** Set value with validation */
  setValue: (value: T) => void;
  /** Current error message */
  error: string | null;
  /** Whether the field is valid */
  isValid: boolean;
  /** Clear the error */
  clearError: () => void;
}

/**
 * Generic hook for validating a single field with a custom validator.
 */
export function useFieldValidation<T>(
  initialValue: T,
  validator: (value: T) => string | null
): UseFieldValidationResult<T> {
  const [value, setValueInternal] = useState<T>(initialValue);
  const [error, setError] = useState<string | null>(null);

  const setValue = useCallback(
    (newValue: T) => {
      setValueInternal(newValue);
      const validationError = validator(newValue);
      setError(validationError);
    },
    [validator]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const isValid = error === null;

  return {
    value,
    setValue,
    error,
    isValid,
    clearError,
  };
}

// ============================================================================
// Validation State Management Hook
// ============================================================================

export interface ValidationState {
  isValidating: boolean;
  hasValidated: boolean;
  lastValidationResult: ValidationResult | null;
}

export interface UseValidationStateResult extends ValidationState {
  /** Start validation (sets isValidating to true) */
  startValidation: () => void;
  /** Complete validation with result */
  completeValidation: (result: ValidationResult) => void;
  /** Reset validation state */
  resetValidation: () => void;
}

/**
 * Hook for managing async validation state.
 * Useful for server-side validation or complex async validators.
 */
export function useValidationState(): UseValidationStateResult {
  const [state, setState] = useState<ValidationState>({
    isValidating: false,
    hasValidated: false,
    lastValidationResult: null,
  });

  const startValidation = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isValidating: true,
    }));
  }, []);

  const completeValidation = useCallback((result: ValidationResult) => {
    setState({
      isValidating: false,
      hasValidated: true,
      lastValidationResult: result,
    });
  }, []);

  const resetValidation = useCallback(() => {
    setState({
      isValidating: false,
      hasValidated: false,
      lastValidationResult: null,
    });
  }, []);

  return {
    ...state,
    startValidation,
    completeValidation,
    resetValidation,
  };
}
